import clsx from '../../utils/clsx';
import styles from '../../styles/Battlefield.module.css';
import { useSyncExternalStore } from 'react';
import { ShowOptions, CameraOptions, shownSummary } from '../battlefieldBuild/ViewOptions';
import { getViewerPrefs, setViewerPref, subscribeViewerPrefs } from '../../utils/viewerPrefs';
import BattlefieldMapAdmin from '../BattlefieldMapAdmin';
import MapIcon from '../MapIcon';
import { MODES } from './usePanelModes';

/** The mode buttons - only the modes on offer to this viewer. */
function ModeButtons({ map }) {
  const { mode, availableModes } = map;
  const enter = (key) => {
    map.clearLine();
    map.setMode(key);
    // Build paints top-down, where what you click is exactly where it
    // lands. At an angle it would not be.
    if (key === 'build') map.cam.applyPreset({ yaw: 0, pitch: 90 });
  };
  return (
    <div className={styles.modeGroup} role="group" aria-label="Battlefield mode">
      {MODES.filter((m) => availableModes.includes(m.key)).map((m) => (
        <button
          key={m.key}
          type="button"
          data-testid={`battlefield-mode-${m.key}`}
          aria-pressed={mode === m.key}
          className={clsx(styles.mode, mode === m.key && styles.modeActive)}
          onClick={() => enter(m.key)}
          title={m.hint}
        >
          <MapIcon name={m.icon} /> <span className={styles.modeLabel}>{m.label}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Which movement band to show for whoever is selected - and WHY the disc
 * is small, or missing: a reach that silently shrinks is a reach nobody
 * trusts.
 */
function MovementPicker({ map }) {
  const { moving } = map;
  if (!map.movingId) return null;
  const why = moving.stopped ? moving.reasons[0] : moving.reasons.join(' · ');
  return (
    <>
      <select
        className={styles.picker}
        aria-label="Show movement"
        data-testid="battlefield-movement"
        value={map.movementMode}
        onChange={(e) => map.setMovementMode(e.target.value)}
      >
        <option value="">Movement…</option>
        <option value="auto">Auto - every band</option>
        <option value="half">Half</option>
        <option value="full">Full</option>
        <option value="charge">Charge</option>
        <option value="run">Run</option>
      </select>
      {moving.reasons.length > 0 && (
        <span
          className={clsx(styles.readoutHint, moving.stopped && styles.readoutBlocked)}
          data-testid="battlefield-movement-conditions"
          title={moving.reasons.join(' · ')}
        >
          {why}
        </span>
      )}
    </>
  );
}

/** The GM's maps: load mid-fight; save, delete and import only while building. */
function MapAdmin({ panel, map }) {
  const load = (next) => (panel.onLoadMap || panel.setTerrain)?.(next);
  return (
    <BattlefieldMapAdmin
      boundary={map.boundary}
      building={map.building}
      library={panel.library}
      onLoad={load}
      onSave={(name) => panel.onSaveMap?.(name)}
      onDeleteSaved={(name) => panel.onDeleteMap?.(name)}
      onExportMaps={(name) => panel.onExportMaps?.(name)}
      onImportMaps={(file) => panel.onImportMaps?.(file)}
      mapFolderFiles={panel.mapFolderFiles}
      onMapFolderFiles={panel.onMapFolderFiles}
      onImportMapEntries={panel.onImportMapEntries}
      sessionLibrary={panel.sessionLibrary}
      tableAssets={panel.tableAssets}
      onImportAscii={load}
    />
  );
}

/**
 * ONE MENU OF THE TOOLBAR (v8.424.0): a button that says what it holds, and
 * its controls after it while it is open. The toolbar was a
 * dozen small toggles wrapping onto three lines; closed, it is the mode
 * and two words. Open or closed is the viewer's habit, kept with their
 * other map preferences.
 */
function ToolbarMenu({ id, label, summary, children }) {
  const prefs = useSyncExternalStore(subscribeViewerPrefs, getViewerPrefs, getViewerPrefs);
  const key = `mapMenu-${id}`;
  const open = prefs[key] === true;
  return (
    <>
      <button
        type="button"
        className={styles.menuToggle}
        data-testid={`battlefield-menu-${id}`}
        aria-expanded={open}
        onClick={() => setViewerPref(key, !open)}
      >
        {label}
        {summary ? `: ${summary}` : ''}
      </button>
      {open && (
        <div className={styles.menuRow} role="group" aria-label={label}>
          {children}
        </div>
      )}
    </>
  );
}

/** Play's and Deploy's view furniture: what the map shows, and the camera. */
function ViewMenus({ view }) {
  if (!view) return null;
  return (
    <>
      <ToolbarMenu id="show" label="Show" summary={shownSummary(view)}>
        <ShowOptions view={view} />
      </ToolbarMenu>
      <ToolbarMenu id="camera" label="Camera">
        <CameraOptions view={view} />
      </ToolbarMenu>
    </>
  );
}

/**
 * WHAT THE MODIFIERS WOULD DO RIGHT NOW. The reference lives in F1, but a
 * reference only helps somebody who already suspects there is something
 * to look up; this teaches at the moment of use (v8.25.0).
 */
function gestureHint({ formationLine, sweepBoxState, deploying }) {
  if (formationLine) return `release to place · hold ALT for a block${formationLine.area ? ' (holding)' : ''}`;
  if (sweepBoxState) return 'release to recall everyone inside';
  if (deploying) return 'right-drag sweeps · shift+right turns the map · F1 for every gesture';
  return 'right-drag turns the map · ? Legend · F1 for every gesture';
}

/**
 * The toolbar under the map. PLAY AND DEPLOY KEEP THEIR OWN FURNITURE down
 * here, folded into two menus (v8.424.0); while building, the view options
 * live in the option bar's View item instead (v8.0.1). `view` is
 * viewOptionsOf's bundle.
 */
export default function PanelToolbar({ panel, map, view }) {
  return (
    <div className={styles.toolbar}>
      <ModeButtons map={map} />
      <MovementPicker map={map} />

      {!map.building && <ViewMenus view={view} />}

      <div className={styles.spacer} />
      {panel.isGm && <MapAdmin panel={panel} map={map} />}
      <span className={styles.readoutHint} data-testid="gesture-hint">
        {gestureHint(map)}
      </span>
    </div>
  );
}
